import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTableDataSearchDto,
  InfoIconDto,
  VegaChartDto,
} from '@asset-metrix/portal-gateway-client-js';
import { WidgetProps } from '@asset-metrix/portal-ui';
import { useLocalStorage } from '@mantine/hooks';
import dayjs from 'dayjs';
import { View } from 'vega';

import { DOWNLOAD_DATE_PREFIX_FORMAT } from '../../config';
import { COLOR_SCHEME_STORAGE_KEY } from '../../utils/constants';
import { BASE_FONT_SIZE } from '../../utils/pixelSizeHelperPortalUi';
import { triggerDownloadWithAnchorTag } from '../../utils/triggerDownloadWithAnchorTag';
import { widgetShareAction } from '../table/widgetShareAction';

const EXPORT_BACKGROUND_LIGHT_THEME = 'white';
const EXPORT_BACKGROUND_DARK_THEME = 'black';
const EXPORT_PADDING = BASE_FONT_SIZE;

export type BaseUseVegaChartActionProps = {
  activeCustomFilterIds: string[];
  customFilterConfigs: VegaChartDto['config']['customFilter'];
  infoIcon?: InfoIconDto;
  title: string;
  widgetId: string;
  closeFullscreen?: () => void;
  openCustomFilter: () => void;
  openInfoModal?: () => void;
  openFullscreen?: () => void;
};

export const useVegaChartActions = ({
  activeCustomFilterIds,
  config,
  customFilterConfigs,
  infoIcon,
  pageFilter,
  title,
  vegaViewRef,
  widgetId,
  closeFullscreen,
  openCustomFilter,
  openInfoModal,
  openFullscreen,
}: BaseUseVegaChartActionProps & {
  config: VegaChartDto['config'];
  pageFilter?: DataTableDataSearchDto['selection'];
  vegaViewRef: RefObject<View>;
}) => {
  const { t } = useTranslation();
  const actions: WidgetProps['actions'] = [];

  if (customFilterConfigs.length > 0) {
    actions.push({
      icon:
        activeCustomFilterIds.length > 0
          ? { name: 'filter-active', svgIcon: true }
          : { name: 'filter', svgIcon: true },
      title: t('app.widget.action.label.customFilter'),
      cb: openCustomFilter,
    });
  }

  const widgetShareActions =
    config.shareActions?.map((sa) =>
      widgetShareAction(widgetId, title, sa, t, {
        pageFilterSelections: pageFilter,
        tableFilterSelections: {},
      })
    ) ?? [];

  const [colorScheme] = useLocalStorage<'dark' | 'light' | undefined>({
    getInitialValueInEffect: false,
    key: COLOR_SCHEME_STORAGE_KEY,
  });

  widgetShareActions.push(
    {
      label: t('app.widget.action.label.imageDownload'),
      cb: async () => {
        const vegaView = vegaViewRef.current;
        if (vegaView != null) {
          triggerDownloadWithAnchorTag(
            await vegaView.toImageURL('png'),
            `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${title || `chart-export-${widgetId}`}.png`
          );
        }
      },
    },
    {
      label: t('app.widget.action.label.imageDownloadWithBackground'),
      cb: async () => {
        const vegaView = vegaViewRef.current;
        if (vegaView != null) {
          const sourceCanvas = await vegaView.toCanvas();

          const canvas = document.createElement('canvas');
          canvas.width = sourceCanvas.width + EXPORT_PADDING;
          canvas.height = sourceCanvas.height + EXPORT_PADDING;

          const context = canvas.getContext('2d');
          if (context) {
            context.fillStyle =
              colorScheme === 'dark'
                ? EXPORT_BACKGROUND_DARK_THEME
                : EXPORT_BACKGROUND_LIGHT_THEME;
            context.fillRect(0, 0, canvas.width, canvas.height);

            context.drawImage(
              sourceCanvas,
              Math.round(EXPORT_PADDING / 2),
              Math.round(EXPORT_PADDING / 2)
            );
          }
          const dataUrl = canvas.toDataURL('png');

          triggerDownloadWithAnchorTag(
            dataUrl,
            `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${title || `chart-export-${widgetId}`}.png`
          );
        }
      },
    }
  );

  actions.push({
    icon: { name: 'share' },
    title: t('app.widget.action.label.shareActions'),
    moreActions: widgetShareActions,
  });

  if (infoIcon != null) {
    actions.push({
      icon: { name: 'info' },
      title: t('app.widget.action.label.additionalInformation'),
      cb: openInfoModal,
    });
  }

  if (openFullscreen) {
    actions.push({
      icon: { name: 'open_in_full' },
      title: t('app.widget.action.label.openInFullscreen'),
      cb: openFullscreen,
    });
  }
  if (closeFullscreen) {
    actions.push({
      icon: { name: 'close' },
      title: t('app.widget.action.label.closeFullscreen'),
      cb: closeFullscreen,
    });
  }

  return actions;
};
